import React, { useState, useEffect, useRef } from 'react';
import { 
  Clock, CheckCircle, AlertCircle, Play, 
  Pause, X, RefreshCw, Download, Upload,
  FileText, Eye, EyeOff, Activity
} from 'lucide-react';
import { createSSEConnection, apiFetch } from '../config/api';
import notificationService from '../utils/notificationService';
import './TaskCenter.css';

// Types minimaux (P1 : suppression de la directive ts-nocheck). Champs souples,
// alimentés par un flux SSE dont la forme n'est pas strictement garantie.
interface TaskProgress {
  percentage?: number;
  stage?: string;
}

interface Task {
  id: string;
  name?: string;
  description?: string;
  type?: string;
  status?: string;
  progress?: TaskProgress;
  current_stage?: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  result?: unknown;
  error?: unknown;
  file_size?: number;
}

interface TaskStats {
  total: number;
  running: number;
  completed: number;
  failed: number;
}

interface TaskEvent {
  type: string;
  task?: Task;
  task_id?: string;
  progress?: TaskProgress;
  result?: unknown;
  error?: unknown;
}

const TaskCenter = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  // `createSSEConnection` ne rend pas une `EventSource` mais une POIGNEE stable
  // dont `close()` ferme toujours la connexion courante, y compris apres une
  // reconnexion. C'est voulu : garder la reference initiale laissait fuir la
  // connexion issue de la reconnexion (cf. frontend/src/config/sse.test.js).
  type ConnexionSSE = { close: () => void; readonly readyState: number };
  const [eventSource, setEventSource] = useState<ConnexionSSE | null>(null);
  const [stats, setStats] = useState<TaskStats>({
    total: 0,
    running: 0,
    completed: 0,
    failed: 0
  });

  const eventSourceRef = useRef<ConnexionSSE | null>(null);

  useEffect(() => {
    loadTasks();
    setupSSE();
    
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // AUD-018 : URL SSE des tâches = /api/tasks/sse/events/tasks (router tasks prefix)
  // [2026-09-09] Ce composant reimplementait sa PROPRE reconnexion, sans fin et
  // sans fermer la connexion morte : `EventSource` retentait deja tout seul, et
  // le `setTimeout` de 5 s ajoutait sa propre tentative par-dessus. Mesure en
  // production : ~22 requetes par minute sur cette route, toutes en 401, 6486
  // en 24 h. On delegue desormais a `createSSEConnection`, qui borne les
  // tentatives, espace les essais et abandonne quand la session a expire.
  const setupSSE = () => {
    try {
      const connexion = createSSEConnection('tasks/sse/events/tasks', handleTaskEvent);
      eventSourceRef.current = connexion;
      setEventSource(connexion);
    } catch (error) {
      console.error('Failed to setup SSE:', error);
    }
  };

  const handleTaskEvent = (data: TaskEvent) => {
    switch (data.type) {
      case 'task_started':
        if (data.task) addTask(data.task);
        break;
      case 'task_progress':
        if (data.task_id) updateTaskProgress(data.task_id, data.progress ?? {});
        break;
      case 'task_completed':
        if (data.task_id) completeTask(data.task_id, data.result);
        break;
      case 'task_failed':
        if (data.task_id) failTask(data.task_id, data.error);
        break;
      case 'task_cancelled':
        if (data.task_id) removeTask(data.task_id);
        break;
      default:
        console.log('Unknown task event:', data);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await apiFetch('tasks/active');
      const data = await response.json();
      
      if (data.success) {
        setTasks(data.tasks || []);
        updateStats(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const addTask = (task: Task) => {
    setTasks(prev => {
      const newTasks = [...prev, task];
      updateStats(newTasks);
      return newTasks;
    });
  };

  const updateTaskProgress = (taskId: string, progress: TaskProgress) => {
    setTasks(prev => {
      const newTasks = prev.map((task): Task =>
        task.id === taskId
          ? {
              ...task,
              progress,
              // exactOptionalPropertyTypes : ne pas poser current_stage=undefined.
              ...(progress.stage !== undefined ? { current_stage: progress.stage } : {}),
            }
          : task
      );
      updateStats(newTasks);
      return newTasks;
    });
  };

  const completeTask = (taskId: string, result: unknown) => {
    setTasks(prev => {
      const newTasks = prev.map(task => 
        task.id === taskId 
          ? { ...task, status: 'completed', result, completed_at: new Date().toISOString() }
          : task
      );
      updateStats(newTasks);
      return newTasks;
    });

    // Remove completed task after 5 seconds
    setTimeout(() => {
      removeTask(taskId);
    }, 5000);
  };

  const failTask = (taskId: string, error: unknown) => {
    setTasks(prev => {
      const newTasks = prev.map((task): Task =>
        task.id === taskId
          ? { ...task, status: 'failed', error, failed_at: new Date().toISOString() }
          : task
      );
      updateStats(newTasks);
      return newTasks;
    });

    notificationService.showToast(`Tâche échouée: ${String(error)}`, 'error');
  };

  const removeTask = (taskId: string) => {
    setTasks(prev => {
      const newTasks = prev.filter(task => task.id !== taskId);
      updateStats(newTasks);
      return newTasks;
    });
  };

  const updateStats = (taskList: Task[]) => {
    const stats = {
      total: taskList.length,
      running: taskList.filter(t => t.status === 'running').length,
      completed: taskList.filter(t => t.status === 'completed').length,
      failed: taskList.filter(t => t.status === 'failed').length
    };
    setStats(stats);
  };

  const cancelTask = async (taskId: string) => {
    try {
      const response = await apiFetch(`tasks/${taskId}/cancel`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        notificationService.showToast('Tâche annulée', 'success');
        removeTask(taskId);
      } else {
        notificationService.showToast('Erreur lors de l\'annulation', 'error');
      }
    } catch (error) {
      notificationService.showToast('Erreur de connexion', 'error');
    }
  };

  const getTaskIcon = (type?: string) => {
    switch (type) {
      case 'ingestion':
        return <Upload size={16} />;
      case 'indexation':
        return <FileText size={16} />;
      case 'backup':
        return <Download size={16} />;
      case 'export':
        return <Upload size={16} />;
      default:
        return <Activity size={16} />;
    }
  };

  const getStageIcon = (stage?: string) => {
    switch (stage) {
      case 'reception':
        return <Download size={16} />;
      case 'conversion':
        return <RefreshCw size={16} />;
      case 'ocr':
        return <FileText size={16} />;
      case 'indexation':
        return <Activity size={16} />;
      case 'terminé':
        return <CheckCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'running':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'cancelled':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatDuration = (startTime?: string) => {
    if (!startTime) return '0s';
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now.getTime() - start.getTime()) / 1000);
    
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m`;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`task-center ${isVisible ? 'visible' : ''}`}>
      {/* Bouton toggle */}
      <button 
        className="task-center-toggle"
        onClick={() => setIsVisible(!isVisible)}
        title="Centre de tâches"
      >
        <Activity size={20} />
        {stats.running > 0 && (
          <span className="task-badge">{stats.running}</span>
        )}
      </button>

      {/* Panneau principal */}
      <div className="task-panel">
        <div className="task-header">
          <h2><Activity /> Centre de Tâches</h2>
          <button 
            onClick={() => setIsVisible(false)}
            className="close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Statistiques */}
        <div className="task-stats">
          <div className="stat-item">
            <span className="stat-label">Total</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item running">
            <span className="stat-label">En cours</span>
            <span className="stat-value">{stats.running}</span>
          </div>
          <div className="stat-item completed">
            <span className="stat-label">Terminées</span>
            <span className="stat-value">{stats.completed}</span>
          </div>
          <div className="stat-item failed">
            <span className="stat-label">Échecs</span>
            <span className="stat-value">{stats.failed}</span>
          </div>
        </div>

        {/* Liste des tâches */}
        <div className="task-list">
          {loading ? (
            <div className="loading">
              <RefreshCw className="spinner" />
              <p>Chargement des tâches...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="no-tasks">
              <Activity size={48} />
              <p>Aucune tâche active</p>
            </div>
          ) : (
            tasks.map(task => (
              <div key={task.id} className={`task-item ${task.status}`}>
                <div className="task-header">
                  <div className="task-info">
                    <div className="task-icon">
                      {getTaskIcon(task.type)}
                    </div>
                    <div className="task-details">
                      <h4>{task.name}</h4>
                      <p>{task.description}</p>
                    </div>
                  </div>
                  <div className="task-actions">
                    {task.status === 'running' && (
                      <button 
                        onClick={() => cancelTask(task.id)}
                        className="cancel-btn"
                        title="Annuler"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Progression */}
                {task.status === 'running' && task.progress && (
                  <div className="task-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ width: `${task.progress.percentage}%` }}
                      ></div>
                    </div>
                    <div className="progress-info">
                      <span className="progress-text">
                        {task.progress.percentage}%
                      </span>
                      <span className="progress-stage">
                        {getStageIcon(task.progress.stage)}
                        {task.progress.stage}
                      </span>
                    </div>
                  </div>
                )}

                {/* Étapes */}
                <div className="task-stages">
                  {['reception', 'conversion', 'ocr', 'indexation', 'terminé'].map(stage => (
                    <div 
                      key={stage} 
                      className={`stage ${getStageStatus(task, stage)}`}
                    >
                      {getStageIcon(stage)}
                      <span>{stage}</span>
                    </div>
                  ))}
                </div>

                {/* Métadonnées */}
                <div className="task-meta">
                  <div className="meta-item">
                    <Clock size={16} />
                    <span>{formatDuration(task.started_at)}</span>
                  </div>
                  {task.file_size && (
                    <div className="meta-item">
                      <FileText size={16} />
                      <span>{formatFileSize(task.file_size)}</span>
                    </div>
                  )}
                  {task.status === 'completed' && !!task.result && (
                    <div className="meta-item success">
                      <CheckCircle size={16} />
                      <span>Succès</span>
                    </div>
                  )}
                  {task.status === 'failed' && !!task.error && (
                    <div className="meta-item error">
                      <AlertCircle size={16} />
                      <span>Échec</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="task-actions">
          <button 
            onClick={loadTasks}
            className="refresh-btn"
            disabled={loading}
          >
            <RefreshCw size={16} />
            Actualiser
          </button>
        </div>
      </div>
    </div>
  );
};

// Fonction utilitaire pour déterminer le statut d'une étape
const getStageStatus = (task: Task, stage: string) => {
  if (task.status === 'failed') return 'failed';
  if (task.status === 'completed') return 'completed';
  
  const stages = ['reception', 'conversion', 'ocr', 'indexation', 'terminé'];
  const currentIndex = stages.indexOf(task.current_stage || 'reception');
  const stageIndex = stages.indexOf(stage);
  
  if (stageIndex < currentIndex) return 'completed';
  if (stageIndex === currentIndex) return 'running';
  return 'pending';
};

export default TaskCenter;
